import { useState } from 'react';
import { X, ShieldAlert, Eye, EyeOff, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

/**
 * DebugPinModal — verifies the ADMIN-ONLY debug PIN (env-configured on the
 * server via DEBUG_PIN). Deliberately kept separate from PinGateModal so a
 * parent entering their normal daily PIN can NEVER accidentally open the
 * scoring-debug surface.
 *
 * Contract:
 *   - No token is issued on success — every future debug-panel open must
 *     enter the PIN again (10 rapid taps + PIN each time).
 *   - On success, calls onUnlocked() → parent screen reveals the panel.
 *   - On cancel, calls onCancel() and nothing changes.
 */
export default function DebugPinModal({ onUnlocked, onCancel }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);

  const submit = async () => {
    setError('');
    if (!pin) return setError('Enter the debug PIN.');
    setBusy(true);
    try {
      const { data } = await api.post('/debug/verify-pin', { pin });
      if (data?.ok) { onUnlocked(pin); return; }
      setError('Wrong debug PIN.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || 'Could not verify. Try again.');
    } finally { setPin(''); setBusy(false); }
  };

  return (
    <div data-testid="debug-pin-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl w-full max-w-sm p-6 relative">
        <button data-testid="debug-pin-close" onClick={onCancel}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X size={20}/></button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-2xl bg-[#2B2140] text-white flex items-center justify-center">
            <ShieldAlert size={22} strokeWidth={2.8}/>
          </div>
          <div>
            <div className="text-lg font-extrabold text-[#2B2140]">Admin debug access</div>
            <div className="text-xs text-slate-500">Not the family Parent/Guardian PIN — a separate admin code.</div>
          </div>
        </div>

        <p className="text-sm text-slate-600 my-3">
          Enter the admin-only debug PIN to open the scoring-debug panel. This is a different code
          from the everyday Parent/Guardian PIN used for camera / new-book uploads.
        </p>

        <div className="relative mb-3">
          <input data-testid="debug-pin-input" autoFocus value={pin}
            onChange={(e) => setPin(e.target.value.slice(0, 12))}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            type={reveal ? 'text' : 'password'} inputMode="text"
            autoComplete="off"
            className="w-full pl-4 pr-12 py-3 rounded-2xl border-2 border-slate-200 focus:border-[#2B2140] outline-none text-lg tracking-widest text-center"
            placeholder="Admin debug PIN"/>
          <button type="button" data-testid="debug-pin-reveal" onClick={() => setReveal(r => !r)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-700"
            aria-label={reveal ? 'Hide PIN' : 'Show PIN'}>
            {reveal ? <EyeOff size={18} strokeWidth={2.5}/> : <Eye size={18} strokeWidth={2.5}/>}
          </button>
        </div>

        {error && <div className="text-sm text-[#E8435C] font-bold mb-2">{error}</div>}

        <button data-testid="debug-pin-submit" onClick={submit} disabled={busy}
          className="pushable pushable-primary w-full bg-[#2B2140] text-white font-extrabold px-6 py-3 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="animate-spin" size={18}/>} Unlock debug panel
        </button>
      </div>
    </div>
  );
}
